import React, { useEffect, useState } from "react";
import { storeApi } from "../services/storeApi";
import { useAuth } from "../hooks/useAuth";
import { useLocalization } from "../hooks/useLocalization";

// Controlled Store selector - replaces free-text store_location fields with a dropdown tied
// to the real Store entity (human-readable name, no raw UUID). Emits the store NAME (not id)
// via onChange, since most existing forms/collections still key off store_location text -
// this keeps the swap a drop-in replacement without a wider schema migration.
//
// Phase 2 P0: also self-restricts the visible options to the current user's own scope (unless
// organization-wide) so a store-scoped manager is never even shown a store they could not
// submit anyway - the backend (scope_service.validate_store_location_for_write /
// can_access_store_location) is still the actual enforcement boundary.
export const StoreSelect = ({ value, onChange, testId, includeInactive = false, allowEmpty = false, emptyLabel, className = "", restrictToScope = true }) => {
  const { user } = useAuth();
  const { t } = useLocalization();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    storeApi.list(includeInactive).then((res) => setStores(res.data)).finally(() => setLoading(false));
  }, [includeInactive]);

  const scope = user?.scope;
  const isOrgWide = !scope || scope.organization_scope === "ORGANIZATION_ALL";
  const visibleStores = restrictToScope && !isOrgWide
    ? stores.filter((s) => (scope.store_ids || []).includes(s.id) || s.name === value)
    : stores;

  return (
    <select
      data-testid={testId || "store-select"}
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={loading}
      className={`w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
    >
      {allowEmpty && <option value="">{emptyLabel || t("common.organizationWideAllStores")}</option>}
      {!allowEmpty && !value && <option value="" disabled>{t("common.selectStore")}</option>}
      {visibleStores.map((s) => (
        <option key={s.id} value={s.name}>
          {s.display_name || s.name}{s.japanese_name ? ` (${s.japanese_name})` : ""}{s.status !== "ACTIVE" ? ` - ${t("common.inactive")}` : ""}
        </option>
      ))}
    </select>
  );
};
